import React, { useEffect, useState } from 'react';
import { FaCheck, FaFileImport, FaLink, FaSearch } from 'react-icons/fa';
import api from '../api';

const input = 'min-h-[44px] w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100';
const button = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50';
const primary = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#102c5a] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#193f77] disabled:opacity-50';
const message = error => error?.response?.data?.error || error?.message || 'Não foi possível concluir esta ação.';

export default function CustomerFormImport({ forms = [], onClose, onImported, embedded = false, onBusyChange }) {
  const [formId, setFormId] = useState('');
  const [definition, setDefinition] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState('UPSERT');
  const [responses, setResponses] = useState([]);
  const [selected, setSelected] = useState([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => { onBusyChange?.(busy || loading); }, [busy, loading, onBusyChange]);

  useEffect(() => {
    if (!formId) { setDefinition(null); setResponses([]); return undefined; }
    let active = true;
    setLoading(true); setError(''); setNotice(''); setPreview(null); setResult(null); setSelected([]);
    Promise.all([
      api.get('/customers/from-form/' + formId + '/fields'),
      api.get('/customers/from-form/' + formId + '/responses', { params: { offset: 0 } }),
    ]).then(([config, list]) => {
      if (!active) return;
      setDefinition(config.data);
      setMapping(config.data?.mapping || {});
      setMode(config.data?.mode || 'UPSERT');
      setResponses(list.data?.items || []);
      setNextOffset(list.data?.nextOffset || 50);
      setHasMore(Boolean(list.data?.hasMore));
    }).catch(err => { if (active) { setError(message(err)); setDefinition(null); setResponses([]); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [formId]);

  const loadMore = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.get('/customers/from-form/' + formId + '/responses', { params: { offset: nextOffset } });
      setResponses(old => [...old, ...(data.items || [])]);
      setNextOffset(data.nextOffset);
      setHasMore(Boolean(data.hasMore));
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  };
  const changeMapping = (target, value) => {
    setMapping(old => ({ ...old, [target]: value }));
    setPreview(null); setResult(null); setNotice('');
  };
  const changeMode = value => { setMode(value); setPreview(null); setResult(null); setNotice(''); };
  const toggle = id => {
    setSelected(old => old.includes(id) ? old.filter(value => value !== id) : [...old, id].slice(0, 100));
    setPreview(null); setResult(null);
  };
  const saveMapping = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      await api.put('/customers/from-form/' + formId + '/config', { mapping, mode });
      setNotice('Mapeamento salvo neste formulário.');
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  };
  const run = async commit => {
    if (!mapping.name || !selected.length || busy) return;
    if (commit && (!preview || preview.failed > 0 && !window.confirm('Existem respostas inválidas. Importar as demais?'))) return;
    if (commit && !window.confirm('Confirmar ' + selected.length + ' respostas? Os clientes existentes poderão ser atualizados conforme o modo selecionado.')) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const { data } = await api.post('/customers/from-form/' + formId + '/' + (commit ? 'commit' : 'preview'),
        { mapping, mode, instanceIds: selected });
      if (commit) { setResult(data); setPreview(null); setNotice('Importação processada.'); await onImported?.(data); }
      else { setPreview(data); setResult(null); }
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  };
  const report = result || preview;
  return <div className={embedded ? "space-y-5 text-slate-800" : "max-h-[76vh] space-y-5 overflow-y-auto p-1 text-slate-800"}>
    {!embedded && <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="mb-1 flex items-center gap-2 text-blue-600"><FaLink/><span className="text-xs font-bold uppercase tracking-wider">Formulários + Clientes</span></div>
        <h2 className="text-xl font-bold text-slate-900">Importar respostas para Clientes</h2>
        <p className="mt-1 text-sm text-slate-500">Selecione um formulário, vincule os campos e confira os dados antes de importar.</p>
      </div>
      <button type="button" onClick={onClose} className={button}>Fechar</button>
    </div>}
    <label className="block text-sm font-semibold">Formulário de origem
      <select aria-label="Formulário de origem" className={input + ' mt-1'} disabled={busy} value={formId} onChange={e => setFormId(e.target.value)}>
        <option value="">Selecione o formulário</option>
        {forms.map(form => <option key={form.id} value={form.id}>{form.name}</option>)}
      </select>
    </label>
    {loading && <p role="status" className="text-sm text-slate-500">Carregando campos e respostas…</p>}
    {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
    {notice && <p role="status" className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</p>}
    {definition && <>
      <section className="space-y-3 rounded-2xl border border-slate-200 p-4">
        <div className="flex items-center gap-2"><FaLink className="text-blue-600"/><h3 className="text-base font-bold">Vincular campos</h3></div>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(definition.targets || {}).map(([key, label]) => <label key={key} className="block text-xs font-semibold text-slate-700">
            {label}{key === 'name' ? ' *' : ''}
            <select aria-label={'Campo para ' + label} className={input + ' mt-1'} value={mapping[key] || ''} disabled={busy} onChange={e => changeMapping(key, e.target.value)}>
              <option value="">Não importar</option>
              {(definition.fields || []).filter(f => mapping[key] === f.id || !Object.entries(mapping).some(([other, value]) => other !== key && value === f.id))
                .map(f => <option key={f.id} value={f.id}>{f.label} ({f.id})</option>)}
            </select>
          </label>)}
        </div>
        <label className="block text-sm font-semibold">Tratamento dos clientes encontrados
          <select className={input + ' mt-1'} value={mode} disabled={busy} onChange={e => changeMode(e.target.value)}>
            <option value="UPSERT">Criar novos e atualizar existentes</option>
            <option value="CREATE_ONLY">Criar apenas novos</option>
            <option value="UPDATE_ONLY">Atualizar apenas existentes</option>
          </select>
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={button} disabled={busy || !mapping.name} onClick={saveMapping}><FaCheck/> Salvar vínculo</button>
          <p className="text-xs text-slate-500">A correspondência é salva no formulário. Alterações no CPF/CNPJ identificam o cliente existente.</p>
        </div>
      </section>
      <section className="space-y-3 rounded-2xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-bold">Respostas concluídas ({selected.length} selecionadas)</h3>
          <button type="button" className={button} disabled={busy} onClick={() => {
            setSelected(responses.slice(0, 100).map(row => row.id)); setPreview(null); setResult(null);
          }}>Selecionar exibidas</button>
        </div>
        {responses.length === 0 ? <p className="text-sm text-slate-500">Nenhuma resposta concluída e acessível encontrada neste formulário.</p>
          : <div className="max-h-56 space-y-1 overflow-y-auto">
            {responses.map(row => <label key={row.id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-100 p-3 text-sm hover:bg-slate-50">
              <input type="checkbox" checked={selected.includes(row.id)} disabled={busy || (!selected.includes(row.id) && selected.length >= 100)} onChange={() => toggle(row.id)} className="h-4 w-4 accent-blue-600" />
              <span className="font-semibold">Solicitação #{row.id}</span>
              <span className="ml-auto text-xs text-slate-500">{row.startedAt ? new Date(row.startedAt).toLocaleDateString('pt-BR') : ''}</span>
            </label>)}
          </div>}
        {hasMore && <button type="button" className={button} disabled={busy} onClick={loadMore}>Carregar mais respostas</button>}
      </section>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={button} disabled={busy || !mapping.name || !selected.length} onClick={() => run(false)}><FaSearch/> Conferir importação</button>
        <button type="button" className={primary} disabled={busy || !preview || !selected.length} onClick={() => run(true)}><FaFileImport/> Confirmar importação</button>
      </div>
      {report && <section className="space-y-2 rounded-xl border border-blue-100 bg-blue-50/40 p-4" aria-label={result ? 'Resultado da importação' : 'Prévia da importação'}>
        <h3 className="text-sm font-bold">{result ? 'Resultado da importação' : 'Prévia da importação'}</h3>
        <p className="text-sm">{report.created} novos · {report.updated} atualizações · {report.skipped} ignorados · {report.failed} com erro</p>
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {(report.rows || []).map(row => <div key={row.instanceId} className="flex flex-wrap gap-2 border-t border-slate-200 py-2 text-xs">
            <span>#{row.instanceId}</span><span className="min-w-0 flex-1 break-words font-medium">{row.name || 'Resposta'}</span>
            <span className={row.error ? 'text-red-700' : 'text-slate-600'}>{row.error || ({ CREATED: 'Novo', UPDATED: 'Atualizar', SKIPPED: 'Ignorar' }[row.action] || row.action)}</span>
          </div>)}
        </div>
      </section>}
    </>}
  </div>;
}
